import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, MapPin } from "lucide-react";

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ leaderboard: [], my_rank: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = user?.city ? { city: user.city } : {};
    api.get("/users/leaderboard", { params })
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, [user?.city]);

  const cityShort = (user?.city || "Knoxville, TN").split(",")[0];

  return (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="brut-card p-6 sm:p-8 bg-[var(--cs-yellow)] flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1">
            <MapPin size={12} /> {cityShort} Top Earners
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Leaderboard.</h1>
          <p className="mt-1 font-semibold">Ranked by total earned in {cityShort}. Streaks break ties.</p>
        </div>
        <div className="brut-card p-4 bg-white">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--cs-ink-2)]">Your rank</div>
          <div className="font-display tabular text-5xl font-black text-[var(--cs-flame)] leading-none">
            #{data.my_rank ?? "—"}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[var(--cs-ink-2)] font-bold uppercase tracking-widest text-sm">Loading…</div>
      ) : data.leaderboard.length === 0 ? (
        <div className="brut-card p-10 text-center">
          <div className="font-display text-2xl font-extrabold">No earners in {cityShort} yet.</div>
          <p className="text-[var(--cs-ink-2)] mt-1">Be the first to top the leaderboard. Claim a task!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.leaderboard.map((row) => {
            const top = row.rank <= 3;
            const medal = row.rank === 1 ? "🥇" : row.rank === 2 ? "🥈" : row.rank === 3 ? "🥉" : null;
            return (
              <div
                key={row.id}
                data-testid={`leaderboard-row-${row.rank}`}
                className={`brut-card p-4 flex items-center gap-4 ${top ? "bg-[var(--cs-money)]" : ""} ${row.is_me ? "ring-4 ring-[var(--cs-flame)] ring-offset-2" : ""}`}
              >
                <div className="font-display tabular text-3xl font-black w-14 text-center">
                  {medal || `#${row.rank}`}
                </div>
                <div className="flex-1">
                  <div className="font-display text-xl font-extrabold">
                    {row.name} {row.is_me && <span className="brut-chip ml-2 bg-[var(--cs-flame)] text-white">You</span>}
                  </div>
                  <div className="text-xs font-semibold text-[var(--cs-ink-2)] flex items-center gap-2 mt-0.5">
                    <MapPin size={12} /> {row.city}
                    <span>·</span>
                    <span>{row.tasks_completed} tasks</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display tabular text-2xl font-black leading-none">
                    ${row.total_earned.toFixed(0)}
                  </div>
                  <div className="text-xs font-bold text-[var(--cs-ink-2)] mt-1 inline-flex items-center gap-1">
                    <Flame size={12} className="text-[var(--cs-flame)]" /> {row.streak_days}d
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
